import { useState } from "react";
import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { assumptionsSchema } from "../types/settingsSchemas";
import type { AssumptionsSettingsData } from "../types/settings";
import { useSettings } from "../contexts/SettingsContext";

/**
 * Field classification for Assumptions form (two-tier visibility)
 *
 * Essential: shown during onboarding + Settings
 * Advanced: shown only in Settings (behind ShowMoreToggle)
 */
export const ASSUMPTIONS_ESSENTIAL_FIELDS = [
  "retirementAge",
  "lifeExpectancy",
  "expectedReturnRate",
  "inflationRate",
  "expensesAtRetirement",
  "cashEquivalentsPct",
  "fixedIncomePct",
  "equitiesPct",
] as const;

export const ASSUMPTIONS_ADVANCED_FIELDS = [
  "realEstateAppreciationRate",
  "otherAssetGrowthRate",
  "depreciationRate",
  "healthcareInflationBuffer",
  "withdrawalOrder",
  "estimatedRetirementTaxRate",
  "desiredLegacyAmount",
  "cashEquivalentsReturn",
  "fixedIncomeReturn",
  "equitiesReturn",
] as const;

/**
 * Shared logic for Assumptions functionality
 * Handles form state, validation, and data management
 */
export function useAssumptionsLogic() {
  const { settings, loading: settingsLoading } = useSettings();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { formData, loading, handleChange, handleBlur } =
    useSettingsRHF<AssumptionsSettingsData>("assumptions", assumptionsSchema);

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  return {
    formData,
    loading: loading || settingsLoading,
    handleChange,
    handleBlur,
    isRetired: settings.profile.isRetired,
    showAdvanced,
    toggleAdvanced,
  };
}
